/**
 * Rendering budget helpers (pure, unit tested): the adaptive device-pixel ratio of the 3D view, a frame-time
 * meter for the optional perf overlay, and the debug flag that shows it (`?perf=1` once, or
 * `localStorage["solaros.editor.perf"] = "1"`).
 *
 * The 3D view renders on demand: a frame is drawn only when something changed (the camera moves, damping or the
 * orbit is running, tiles arrive, the design changes, the canvas resizes); an idle editor draws nothing and the
 * GPU sleeps. While it does draw, `AdaptiveDpr` watches the frame time and lowers the pixel ratio in steps when
 * frames are slow (from the device's ratio, capped at 2, down to 1, then 0.75), and raises it again after a run
 * of fast frames, so a laptop on battery stays fluid and a desktop GPU stays sharp.
 */

export interface DprOptions {
  /** Highest ratio used (the screen's, capped). */
  max: number;
  min: number;
  step: number;
  /** Frame time above which the ratio is lowered (ms; 60 fps ≈ 16.7 ms). */
  slowMs: number;
  /** Frame time under which the ratio may rise again. */
  fastMs: number;
  /** Frames averaged before a decision. */
  window: number;
  /** Consecutive fast windows needed to raise the ratio (raising is cautious: no oscillation). */
  fastWindows: number;
}

export const DPR_DEFAULTS: DprOptions = { max: 2, min: 0.75, step: 0.25, slowMs: 22, fastMs: 12, window: 20, fastWindows: 3 };

export function capDpr(deviceRatio: number, cap = DPR_DEFAULTS.max): number {
  return Math.max(1, Math.min(cap, deviceRatio || 1));
}

export class AdaptiveDpr {
  readonly options: DprOptions;
  ratio: number;
  private samples: number[] = [];
  private fastRuns = 0;

  constructor(deviceRatio: number, options: Partial<DprOptions> = {}) {
    this.options = { ...DPR_DEFAULTS, ...options };
    this.options.max = capDpr(deviceRatio, this.options.max);
    this.ratio = this.options.max;
  }

  /** Records one drawn frame's duration (ms between two drawn frames while drawing continuously); returns the
   * new ratio when it changed, else null. Idle gaps (> 250 ms) are not frames and are ignored. */
  sample(frameMs: number): number | null {
    if (!(frameMs > 0) || frameMs > 250) return null;
    this.samples.push(frameMs);
    if (this.samples.length < this.options.window) return null;
    const sorted = [...this.samples].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    this.samples = [];
    const { min, max, step, slowMs, fastMs, fastWindows } = this.options;
    if (median > slowMs && this.ratio > min) {
      this.fastRuns = 0;
      this.ratio = Math.max(min, round(this.ratio - step));
      return this.ratio;
    }
    if (median < fastMs && this.ratio < max) {
      if (++this.fastRuns >= fastWindows) {
        this.fastRuns = 0;
        this.ratio = Math.min(max, round(this.ratio + step));
        return this.ratio;
      }
      return null;
    }
    this.fastRuns = 0;
    return null;
  }
}

const round = (v: number) => Math.round(v * 100) / 100;

/** Rolling frame statistics for the overlay. */
export class FrameMeter {
  private times: number[] = [];
  private size: number;
  frames = 0;

  constructor(size = 60) {
    this.size = size;
  }

  add(frameMs: number): void {
    this.frames++;
    if (frameMs > 250) return; // idle gap
    this.times.push(frameMs);
    if (this.times.length > this.size) this.times.shift();
  }

  stats(): { fps: number; avgMs: number; p95Ms: number } {
    if (!this.times.length) return { fps: 0, avgMs: 0, p95Ms: 0 };
    const avg = this.times.reduce((s, t) => s + t, 0) / this.times.length;
    const sorted = [...this.times].sort((a, b) => a - b);
    return { fps: 1000 / avg, avgMs: avg, p95Ms: sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]! };
  }
}

const PERF_KEY = "solaros.editor.perf";

/** Whether the perf overlay is on: `?perf=1` (remembered) or `?perf=0` (forgotten), else the stored choice. */
export function perfDebugEnabled(search: string = typeof location !== "undefined" ? location.search : ""): boolean {
  const flag = new URLSearchParams(search).get("perf");
  try {
    if (flag === "1") localStorage.setItem(PERF_KEY, "1");
    if (flag === "0") localStorage.removeItem(PERF_KEY);
    return flag === "1" || (flag !== "0" && localStorage.getItem(PERF_KEY) === "1");
  } catch {
    return flag === "1";
  }
}

/** A global counter the views bump, read by the overlay (React commits of the 2D view, 3D frames drawn). */
export const perfCounters = { topViewRenders: 0, threeFrames: 0, threeDpr: 0, threeFrameMs: 0 };
